import {
    ChangeDetectionStrategy,
    Component,
    computed,
    effect,
    inject,
    model,
    ModelSignal,
    signal,
    Signal,
    untracked,
    WritableSignal
} from '@angular/core';
import { MatSelect, MatSelectModule } from '@angular/material/select';
import { AppContext } from 'src/app/app/context';
import { DataType } from 'src/app/pbir/dataModel/DataType';
import { Predicate, PredicateType } from 'src/app/pbir/utils/Predicate';
import { VisualFilter } from 'src/app/pbir/visuals/VisualFilter';
import { AdvancedFilterConditionOperator, DataTimeFormat, TimestampPrecision } from 'src/app/services/api.services';
import { DataTypeMap, OperatorConfig, OperatorValueRequirement } from 'src/app/shared/compatibility/filter.models';
import { SharedModule } from 'src/app/shared/shared.module';

@Component({
  selector: 'filter-editor__advanced',
  template: `
    @for (c of conditions(); track $index) {
      <div class="visual-editor-box__input">
        @if ($first) {
          <label>Show items when the value</label>
        }
        <mat-select class="trg-outline user-select-none w-100p h-24"
                    panelClass="trg-outline-dropdown"
                    [(ngModel)]="c.type"
                    (ngModelChange)="updatePredicateConditions()">
          @if (!$first) {
            <mat-option [value]="NONE" title="None">
              <span>None</span>
            </mat-option>
          }

          @for (o of operators(); track o.id) {
            <mat-option [value]="o.id" [title]="o.title">
              <span>{{o.title}}</span>
            </mat-option>
          }
        </mat-select>

        @if (c.type != NONE && c.valueRequired) {
          <app-data-input
            class="d-contents"
            [appearance]="''"
            [inputClass]="'trg-outline w-100p h-24 mt-5'"
            [type]="dataType().value"
            [(value)]="c.value"
            (valueChange)="updatePredicateConditions()"
            [updateOn]="'blur'"
            [showTimePicker]="showTimePicker()"
            [showSeconds]="showSeconds()"
            [locale]="appContext.localeId()"></app-data-input>
        }
      </div>

      @if (!$last) {
        <div class="logical-operator">
          @let logicalOperator = predicate().logicalOperator ?? LogicalOperator.And;

          <trg-checkbox size="tiny" appearance="circle"
                  [value]="logicalOperator === LogicalOperator.And"
                  (click)="updatePredicate({ logicalOperator: LogicalOperator.And })"
                  [hideBorder]="true">
            And
          </trg-checkbox>

          <trg-checkbox size="tiny" appearance="circle" class="ml-8"
                  [value]="logicalOperator === LogicalOperator.Or"
                  (click)="updatePredicate({ logicalOperator: LogicalOperator.Or })"
                  [hideBorder]="true">
            Or
          </trg-checkbox>
        </div>
      }
    }
  `,
  styles: [`
    :host {
      display: contents;
    }

    ::ng-deep filter-editor__advanced { 
      .logical-operator {
        margin-bottom: 6px;
      
        .trg-checkbox__text {
          font-size: 11px !important;
          color: #2e2e2e !important;
          position: relative;
          top: -1px;
        }
      }

      app-data-input .filter-input {
        width: 100%;

        .mat-datepicker-toggle i {  
          font-size: 13px !important;
          top: -3px !important;
          left: -1px !important;
        }
      }
    }
  `],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [MatSelectModule, SharedModule],
  standalone: true
})
export class AdvancedFilterComponent {
  readonly appContext = inject(AppContext);

  readonly LogicalOperator = Predicate.LogicalOperator;
  readonly OperatorValueRequirement = OperatorValueRequirement;
  readonly NONE: any = {};

  readonly predicate = model<Partial<VisualFilter.AdvancedPredicate>>({
    logicalOperator: Predicate.LogicalOperator.And,
    conditions: [{
      type: Predicate.ConditionType.IsNotBlank,
    }],
  });

  updatePredicate(changes: Partial<VisualFilter.AdvancedPredicate>) {
    let predicate = {
      ...(this.predicate() ?? {}),
      ...changes
    };

    this.predicate.set({
      type: predicate.type,
      target: predicate.target,
      logicalOperator: predicate.logicalOperator,
      conditions: predicate.conditions,
    });
  }


  readonly conditions = computed<ReadonlyArray<ConditionModel>>(() => {
    let conditions = this.predicate()?.conditions
      ?.map((c, i) => new ConditionModel(this, {
        ...c,
        type: c?.type ?? (i < 1 ? Predicate.ConditionType.IsNotBlank : this.NONE)
      })) ?? [];

    if (conditions.length < 1) {
      conditions.push(new ConditionModel(this, { type: Predicate.ConditionType.IsNotBlank }));
    }
    
    if (conditions.length < 2) {
      conditions.push(new ConditionModel(this, { type: this.NONE }));
    }

    return conditions;
  });

  updatePredicateConditions() {
    let conditions = this.conditions()
      .filter(c => c.type != this.NONE)
      .map(c => c.toCondition());

    this.updatePredicate({ conditions });
  }


  readonly target = computed(() => this.predicate()?.target);
  readonly dataType = computed(() => this.target()?.dataType ?? DataType.Unknown);

  readonly operators = computed(() => {
    let dataType = this.dataType();
    return this.getOperators(dataType);
  });

  private getOperators(dataType: DataType) {
    if (!dataType)
      return [];

    return DataTypeMap[dataType.value]?.operators?.advancedCondition ?? [];
  }

  readonly showTimePicker = computed(() => this.dataType().isTimeOrDateTime);
  readonly showSeconds = computed(() => this.showTimePicker() && this.target().hasTimestampPrecision(TimestampPrecision.Seconds));
}

class ConditionModel {
  readonly type: Predicate.ConditionType;
  readonly value: any;
  readonly valueRequired: boolean;

  constructor(component: AdvancedFilterComponent, c?: Predicate.Condition) { 
    this.type = c?.type ?? Predicate.ConditionType.IsNotBlank;
    this.value = c?.value ?? '';

    let operatorConfig = component.operators()?.find(c => c.id == <any>this.type);
    this.valueRequired =  operatorConfig && operatorConfig?.valueRequirement != OperatorValueRequirement.NoValue;
  }

  toCondition(): Predicate.Condition {
    return {
      type: this.type,
      value: this.value
    };
  }
}
